import {
	IconActivity,
	IconAlignLeft,
	IconBriefcase,
	IconCalendar,
	IconChartBar,
	IconClipboardCheck,
	IconClipboardText,
	IconCloud,
	IconExternalLink,
	IconEye,
	IconNotes,
	IconNumber123,
	IconRobotFace,
	IconRocket,
	IconShieldCheck,
	IconToggleRight,
} from "@tabler/icons-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Space, Typography } from "antd";
import { format } from "date-fns";
import { useState } from "react";
import { useAgents } from "@/common/artemis-operations/agents";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { useOrgUsers } from "@/common/artemis-operations/members";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import { useProject, useProjects } from "@/common/artemis-operations/projects";
import { useUpdateTask } from "@/common/artemis-operations/tasks";
import type { ArtemisTask } from "@/common/model";
import { TaskStatus, TERMINAL_TASK_STATUSES } from "@/common/model";
import { toLabel } from "@/common/utils";
import { AssigneeSelectorDrawer } from "@/components/agents/assignee-selector-drawer";
import { CustomEmpty, CustomIcon, IdField } from "../base";
import { BaseDetails, EditableClickable, EditableText } from "../base-details";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import { ResourceLinksGrid } from "../resource-links";
import { TaskStatusBadge, VerificationStatusBadge } from "./task-badges";
import { TaskWorklog } from "./task-worklog";

interface TaskDetailsProps {
	task: ArtemisTask;
	workspaceId: string;
	/** Show the description field. Off in the drawer, where space is tight. */
	showDescription?: boolean;
	onSaveName?: (value: string) => void;
	onSaveDescription?: (value: string) => void;
	onSaveAssignee?: (id: string, type: "AGENT" | "USER") => void;
}

/** Read-only or editable detail view for a single task. Fields without an onSave are read-only. */
export const TaskDetails = ({
	task,
	workspaceId,
	showDescription = true,
	onSaveName,
	onSaveDescription,
	onSaveAssignee,
}: TaskDetailsProps) => {
	const { data: agents } = useAgents(workspaceId);
	const orgSlug = useRouteOrgSlug();
	const { data: projects } = useProjects(workspaceId);
	const { data: projectDetail } = useProject(task.projectId ?? undefined);
	const { data: environments } = useEnvironments(workspaceId);
	const { data: orgUsers } = useOrgUsers();
	const assignee = agents?.find((a) => a?.id === task.assigneeId) ?? null;
	const project = projects?.find((p) => p?.id === task.projectId) ?? null;

	// Task → deployments → environments. An empty deploymentIds list means "all deployments"
	// (see schema), so fall back to every deployment in the project.
	const deploymentIds = task.deploymentIds ?? [];
	const targetDeployments = (projectDetail?.deployments ?? []).filter(
		(d) => deploymentIds.length === 0 || deploymentIds.includes(d.id),
	);
	const taskEnvironments = [
		...new Set([
			...targetDeployments.map((d) => d.environmentId),
			...(task.environmentIds ?? []),
		]),
	]
		.map((id) => environments?.find((e) => e?.id === id))
		.filter((e): e is NonNullable<typeof e> => e != null);
	const [assigneeDrawerOpen, setAssigneeDrawerOpen] = useState(false);
	const agent = agents?.find((a) => a?.id === task.assigneeId);
	const assigneeUser =
		task.assigneeType === "USER"
			? ((orgUsers ?? []).find((u) => u.id === task.assigneeId) ?? null)
			: null;

	// Terminal tasks reject updates server-side, so don't offer editing at all.
	const isTerminal = TERMINAL_TASK_STATUSES.includes(task.status);
	const canEditAssignee =
		onSaveAssignee != null && task.status === TaskStatus.CREATED;

	return (
		<BaseDetails
			item={task}
			fields={[
				{
					key: "id",
					icon: IconNumber123,
					tooltip: "Id",
					render: (id) => <IdField id={id} showCopyButton />,
				},
				{
					key: "name",
					tooltip: "Name",
					icon: IconClipboardText,
					render: (name) => (
						<EditableText onChange={isTerminal ? undefined : onSaveName}>
							{toLabel(name as string)}
						</EditableText>
					),
				},
				{
					key: "description",
					tooltip: "Description",
					icon: IconAlignLeft,
					hidden: () => !showDescription,
					render: (description: string) => (
						<EditableText onChange={isTerminal ? undefined : onSaveDescription}>
							{toLabel(description)}
						</EditableText>
					),
				},
				{
					key: "status",
					tooltip: "Task status",
					icon: IconToggleRight,
					render: (status) => (
						<Space wrap>
							<TaskStatusBadge status={status} />
							{status === TaskStatus.APPROVAL_REQUIRED && (
								<Link
									to={`/${orgSlug}/workspaces/${workspaceId}/approvals` as "/"}
									search={{ search: task.id }}
								>
									View approval
								</Link>
							)}
						</Space>
					),
				},
				{
					key: "projectId",
					tooltip: "Project",
					icon: IconBriefcase,
					hidden: () => !task.projectId,
					render: () => (
						<IdField
							id={project?.name}
							link={`/${orgSlug}/workspaces/${workspaceId}/projects/${project?.id}`}
						/>
					),
				},
				{
					key: "deploymentIds",
					label: "Deployments",
					tooltip: "Deployments",
					icon: IconRocket,
					hidden: () => targetDeployments.length === 0,
					render: () =>
						// An empty deploymentIds list is implicit "all deployments" — say so, otherwise
						// it looks identical to a task explicitly scoped to every deployment.
						deploymentIds.length === 0 ? (
							<Typography.Text type="secondary">
								All ({targetDeployments.map((d) => d.name).join(", ")})
							</Typography.Text>
						) : (
							<Space wrap size={[8, 4]}>
								{targetDeployments.map((d) => (
									<Typography.Text key={d.id}>{d.name}</Typography.Text>
								))}
							</Space>
						),
				},
				{
					key: "environmentIds",
					label: "Environments",
					tooltip: "Environments",
					icon: IconCloud,
					hidden: () => taskEnvironments.length === 0,
					render: () => (
						<Space wrap size={[8, 4]}>
							{taskEnvironments.map((env) => (
								<IdField
									key={env.id}
									id={env.name}
									link={`/${orgSlug}/workspaces/${workspaceId}/environments/${env.id}`}
								/>
							))}
						</Space>
					),
				},
				{
					key: "assigneeId",
					tooltip: "Assignee",
					icon: IconRobotFace,
					render: () => {
						const content = task.assigneeId ? (
							task.assigneeType === "USER" ? (
								<Typography.Text>
									{assigneeUser
										? assigneeUser.displayName || assigneeUser.email
										: task.assigneeId}
								</Typography.Text>
							) : assignee ? (
								<IdField
									id={agent?.name}
									link={`/${orgSlug}/workspaces/${task.workspaceId}/agents/${task.assigneeId}`}
								/>
							) : (
								<Typography.Text type="secondary">
									{task.assigneeId}
								</Typography.Text>
							)
						) : (
							<Typography.Text type="secondary">-</Typography.Text>
						);
						return (
							<>
								{canEditAssignee ? (
									<EditableClickable
										onClick={() => setAssigneeDrawerOpen(true)}
									>
										{content}
									</EditableClickable>
								) : (
									content
								)}
								{canEditAssignee && (
									<AssigneeSelectorDrawer
										open={assigneeDrawerOpen}
										workspaceId={workspaceId}
										assigneeId={task.assigneeId ?? undefined}
										assigneeType={
											(task.assigneeType as "AGENT" | "USER") ?? undefined
										}
										onClose={() => setAssigneeDrawerOpen(false)}
										onConfirm={(id, type) => {
											setAssigneeDrawerOpen(false);
											onSaveAssignee?.(id, type);
										}}
									/>
								)}
							</>
						);
					},
				},
				{
					key: "createdAt",
					tooltip: "Created",
					icon: IconCalendar,
					render: (createdAt) =>
						format(new Date(createdAt as string), "MM/dd/yyyy h:mm a"),
				},
				{
					key: "metrics",
					label: "Usage",
					tooltip: "Usage",
					icon: IconChartBar,
					hidden: (m) =>
						!m ||
						((m.inputTokens ?? 0) === 0 &&
							(m.outputTokens ?? 0) === 0 &&
							(m.timeSpentInSeconds ?? 0) === 0),
					render: (m) =>
						m ? (
							<Typography.Text type="secondary">
								{m.inputTokens?.toLocaleString()} in ·{" "}
								{m.outputTokens?.toLocaleString()} out · {m.timeSpentInSeconds}s
							</Typography.Text>
						) : (
							"-"
						),
				},
				{
					key: "worklog",
					label: "Activity",
					tooltip: "Activity",
					icon: IconActivity,
					render: () => <TaskWorklog task={task} />,
				},
			]}
		/>
	);
};

export const VerificationDetails = ({ task }: { task?: ArtemisTask }) => {
	const hasVerification =
		task &&
		(!!task.verificationCriteria ||
			!!task.verificationStatus ||
			!!task.verificationNotes);
	if (!hasVerification) return <CustomEmpty description="Verification" />;
	return (
		<BaseDetails
			item={task}
			fields={[
				{
					key: "verificationCriteria",
					tooltip: "Verification Criteria",
					icon: IconClipboardCheck,
					render: (v) => toLabel(v),
				},
				{
					key: "verificationStatus",
					tooltip: "Verification Status",
					icon: IconShieldCheck,
					render: (v) => (v ? <VerificationStatusBadge status={v} /> : "-"),
				},
				{
					key: "verificationNotes",
					tooltip: "Verification Notes",
					icon: IconNotes,
					render: (v) => toLabel(v),
				},
			]}
		/>
	);
};

export const TaskDetailsDrawer = ({
	task,
	open,
	onClose,
	onSaveName,
}: {
	task?: ArtemisTask;
	open: boolean;
	onClose: () => void;
	onSaveName?: (value: string) => void;
}) => {
	const orgSlug = useRouteOrgSlug();
	const navigate = useNavigate();

	if (!task) return null;
	return (
		<CustomDrawer
			title={toLabel(task.name)}
			open={open}
			extra={
				<Space.Compact>
					<Button
						type="text"
						icon={<CustomIcon icon={IconEye} tooltip="View" />}
						onClick={() =>
							navigate({
								to: `/${orgSlug}/workspaces/${task.workspaceId}/tasks/${task.id}`,
							})
						}
					/>
					<Button
						type="text"
						icon={
							<CustomIcon
								icon={IconExternalLink}
								tooltip="Open in new window"
							/>
						}
						onClick={() =>
							window.open(
								`/${orgSlug}/workspaces/${task.workspaceId}/tasks/${task.id}`,
								"_blank",
								"noopener",
							)
						}
					/>
				</Space.Compact>
			}
			onClose={onClose}
		>
			<TaskDetails
				task={task}
				workspaceId={task.workspaceId}
				showDescription={false}
				onSaveName={onSaveName}
			/>
		</CustomDrawer>
	);
};

/** Displays the links for a task; clicking a card opens a drawer to edit or delete it. */
export function TaskLinks({
	task,
	onAdd,
}: {
	task: NonNullable<ArtemisTask>;
	/** Opens the add-link dialog from the empty state. */
	onAdd?: () => void;
}) {
	const updateTask = useUpdateTask(task.id);

	return (
		<ResourceLinksGrid
			links={task.links ?? []}
			onSave={(links) => updateTask({ links })}
			onAdd={onAdd}
		/>
	);
}
